/**
 * @file wq-pdp.js
 * @description Product Detail Page interactions: tab switching,
 *              image gallery thumbnails, quantity stepper.
 * @sprint 6 | Task 6.1
 */
window.WQ = window.WQ || {};

window.WQ.PDP = (() => {
  'use strict';

  /* ── Tab switching ── */
  function initTabs(container) {
    const tabList = container.querySelector('[role="tablist"]');
    if (!tabList) return;

    const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
    const panels = tabs.map(tab =>
      container.querySelector('#' + tab.getAttribute('aria-controls'))
    );

    function activateTab(index) {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.classList.toggle('wq-pdp__tab-btn--active', active);
        tab.tabIndex = active ? 0 : -1;
        if (panels[i]) {
          panels[i].classList.toggle('wq-pdp__tab-panel--active', active);
        }
      });
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activateTab(i));
    });

    tabList.addEventListener('keydown', (e) => {
      const currentIndex = tabs.findIndex(t => t === document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        nextIndex = 0;
      } else if (e.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      e.preventDefault();
      tabs[nextIndex].focus();
      activateTab(nextIndex);
    });
  }

  /* ── Image gallery ── */
  function initGallery(container) {
    const mainImg = container.querySelector('.wq-pdp__gallery-main img');
    const thumbs = container.querySelectorAll('.wq-pdp__gallery-thumb');
    if (!mainImg || thumbs.length === 0) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        const alt = thumb.dataset.alt || '';
        if (src) {
          mainImg.src = src;
          mainImg.alt = alt;
        }
        thumbs.forEach(t => t.classList.remove('wq-pdp__gallery-thumb--active'));
        thumb.classList.add('wq-pdp__gallery-thumb--active');
      });
    });
  }

  /* ── Quantity stepper ── */
  function initQuantity(container) {
    const minusBtn = container.querySelector('.wq-pdp__qty-btn[data-action="minus"]');
    const plusBtn = container.querySelector('.wq-pdp__qty-btn[data-action="plus"]');
    const input = container.querySelector('.wq-pdp__qty-input');
    if (!minusBtn || !plusBtn || !input) return;

    function updateQty(delta) {
      let val = parseInt(input.value, 10) || 1;
      val = Math.max(1, Math.min(99, val + delta));
      input.value = val;
    }

    minusBtn.addEventListener('click', () => updateQty(-1));
    plusBtn.addEventListener('click', () => updateQty(1));
    input.addEventListener('change', () => {
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 99) val = 99;
      input.value = val;
    });
  }

  /* ── Add to Cart ── */
  function initATC(container) {
    const btn = container.querySelector('.wq-pdp__atc');
    const qtyInput = container.querySelector('.wq-pdp__qty-input');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const variantId = parseInt(btn.dataset.variantId, 10);
      const qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;

      if (!variantId || !window.WQ.Cart) return;

      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        await window.WQ.Cart.addItem(variantId, qty);
        btn.textContent = 'Added!';
        btn.classList.add('wq-pdp__atc--added');
        setTimeout(() => {
          btn.textContent = 'Add to Cart';
          btn.classList.remove('wq-pdp__atc--added');
          btn.disabled = false;
        }, 1500);
      } catch (err) {
        console.error('WQ PDP ATC error:', err);
        btn.textContent = 'Error — Try Again';
        btn.disabled = false;
      }
    });
  }

  /* ── Variant picker ── */
  function initVariants(container) {
    const variantBtns = container.querySelectorAll('.wq-pdp__variant-btn');
    const atcBtn = container.querySelector('.wq-pdp__atc');
    const priceEl = container.querySelector('.wq-pdp__price');
    const skuEl = container.querySelector('.wq-pdp__sku');

    if (variantBtns.length === 0) return;

    variantBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('wq-pdp__variant-btn--unavailable')) return;

        const group = btn.closest('.wq-pdp__variant-group');
        if (group) {
          group.querySelectorAll('.wq-pdp__variant-btn').forEach(b =>
            b.classList.remove('wq-pdp__variant-btn--active')
          );
        }
        btn.classList.add('wq-pdp__variant-btn--active');

        /* Update variant data */
        const variantId = btn.dataset.variantId;
        const variantPrice = btn.dataset.variantPrice;
        const variantSku = btn.dataset.variantSku;
        const variantAvailable = btn.dataset.variantAvailable;

        if (atcBtn && variantId) {
          atcBtn.dataset.variantId = variantId;
          atcBtn.disabled = variantAvailable === 'false';
          atcBtn.textContent = variantAvailable === 'false' ? 'Sold Out' : 'Add to Cart';
        }

        if (priceEl && variantPrice) {
          const priceSpan = priceEl.querySelector('.wq-pdp__price-regular, .wq-pdp__price-sale');
          if (priceSpan) priceSpan.textContent = variantPrice;
        }

        if (skuEl && variantSku) {
          skuEl.textContent = variantSku;
        }
      });
    });
  }

  /* ── Document preview toggles (PDF + STL) ── */
  function initPreviewToggles(container) {
    const previewBtns = container.querySelectorAll('.wq-pdp-docs__preview-btn');
    previewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const targetId = btn.dataset.previewTarget;
        const panel = document.getElementById(targetId);
        if (!panel) return;

        const isHidden = panel.hidden;
        panel.hidden = !isHidden;
        btn.setAttribute('aria-expanded', String(isHidden));

        /* Lazy-load PDF iframe on first open */
        if (isHidden) {
          const iframe = panel.querySelector('iframe[data-src]');
          if (iframe && !iframe.src) {
            iframe.src = iframe.dataset.src;
          }

          /* Initialize STL viewer on first open */
          const canvas = panel.querySelector('.wq-pdp-docs__stl-canvas');
          const stlUrl = btn.dataset.stlUrl;
          if (canvas && stlUrl && !canvas.dataset.initialized) {
            canvas.dataset.initialized = 'true';
            initSTLViewer(canvas, stlUrl);
          }
        }
      });
    });
  }

  /* ── Lightweight STL viewer (WebGL) ── */
  function initSTLViewer(canvas, stlUrl) {
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      canvas.parentElement.innerHTML = '<p style="padding:20px;text-align:center;color:#6b7280;">WebGL not supported. Please download the STL file to view in a 3D application.</p>';
      return;
    }

    /* State */
    let rotX = -0.5, rotY = 0.3, zoom = 2.0;
    let dragging = false, lastX = 0, lastY = 0;
    let vertices = null, normals = null;

    /* Shaders */
    const vsSrc = `
      attribute vec3 aPosition;
      attribute vec3 aNormal;
      uniform mat4 uMVP;
      uniform mat3 uNormalMatrix;
      varying vec3 vNormal;
      void main() {
        gl_Position = uMVP * vec4(aPosition, 1.0);
        vNormal = normalize(uNormalMatrix * aNormal);
      }`;
    const fsSrc = `
      precision mediump float;
      varying vec3 vNormal;
      void main() {
        vec3 light = normalize(vec3(0.5, 0.8, 1.0));
        float diff = max(dot(vNormal, light), 0.0);
        float ambient = 0.3;
        vec3 color = vec3(0.6, 0.7, 0.85);
        gl_FragColor = vec4(color * (ambient + diff * 0.7), 1.0);
      }`;

    function compileShader(src, type) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    }

    const prog = gl.createProgram();
    gl.attachShader(prog, compileShader(vsSrc, gl.VERTEX_SHADER));
    gl.attachShader(prog, compileShader(fsSrc, gl.FRAGMENT_SHADER));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const aPos = gl.getAttribLocation(prog, 'aPosition');
    const aNorm = gl.getAttribLocation(prog, 'aNormal');
    const uMVP = gl.getUniformLocation(prog, 'uMVP');
    const uNormalMatrix = gl.getUniformLocation(prog, 'uNormalMatrix');

    gl.enable(gl.DEPTH_TEST);
    gl.clearColor(0.96, 0.97, 0.98, 1.0);

    /* Parse binary STL */
    function parseSTL(buffer) {
      const dv = new DataView(buffer);
      const numTriangles = dv.getUint32(80, true);
      const verts = new Float32Array(numTriangles * 9);
      const norms = new Float32Array(numTriangles * 9);
      let offset = 84;
      for (let i = 0; i < numTriangles; i++) {
        const nx = dv.getFloat32(offset, true);
        const ny = dv.getFloat32(offset + 4, true);
        const nz = dv.getFloat32(offset + 8, true);
        offset += 12;
        for (let j = 0; j < 3; j++) {
          const idx = i * 9 + j * 3;
          verts[idx] = dv.getFloat32(offset, true);
          verts[idx + 1] = dv.getFloat32(offset + 4, true);
          verts[idx + 2] = dv.getFloat32(offset + 8, true);
          norms[idx] = nx;
          norms[idx + 1] = ny;
          norms[idx + 2] = nz;
          offset += 12;
        }
        offset += 2; /* attribute byte count */
      }
      return { verts, norms };
    }

    /* Center and scale model */
    function normalizeVertices(verts) {
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      for (let i = 0; i < verts.length; i += 3) {
        minX = Math.min(minX, verts[i]);
        minY = Math.min(minY, verts[i+1]);
        minZ = Math.min(minZ, verts[i+2]);
        maxX = Math.max(maxX, verts[i]);
        maxY = Math.max(maxY, verts[i+1]);
        maxZ = Math.max(maxZ, verts[i+2]);
      }
      const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2;
      const scale = 2.0 / Math.max(maxX - minX, maxY - minY, maxZ - minZ);
      for (let i = 0; i < verts.length; i += 3) {
        verts[i] = (verts[i] - cx) * scale;
        verts[i+1] = (verts[i+1] - cy) * scale;
        verts[i+2] = (verts[i+2] - cz) * scale;
      }
    }

    /* Matrix helpers (minimal, no library) */
    function mat4Perspective(fov, aspect, near, far) {
      const f = 1.0 / Math.tan(fov / 2);
      const nf = 1 / (near - far);
      return new Float32Array([f/aspect,0,0,0, 0,f,0,0, 0,0,(far+near)*nf,-1, 0,0,2*far*near*nf,0]);
    }
    function mat4Identity() { return new Float32Array([1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1]); }
    function mat4Multiply(a, b) {
      const r = new Float32Array(16);
      for (let i = 0; i < 4; i++)
        for (let j = 0; j < 4; j++) {
          r[j*4+i] = 0;
          for (let k = 0; k < 4; k++) r[j*4+i] += a[k*4+i] * b[j*4+k];
        }
      return r;
    }
    function mat4RotateX(m, angle) {
      const c = Math.cos(angle), s = Math.sin(angle);
      const r = mat4Identity(); r[5]=c; r[6]=s; r[9]=-s; r[10]=c;
      return mat4Multiply(m, r);
    }
    function mat4RotateY(m, angle) {
      const c = Math.cos(angle), s = Math.sin(angle);
      const r = mat4Identity(); r[0]=c; r[2]=-s; r[8]=s; r[10]=c;
      return mat4Multiply(m, r);
    }
    function mat4Translate(m, x, y, z) {
      const r = mat4Identity(); r[12]=x; r[13]=y; r[14]=z;
      return mat4Multiply(m, r);
    }
    function mat3FromMat4(m) {
      return new Float32Array([m[0],m[1],m[2], m[4],m[5],m[6], m[8],m[9],m[10]]);
    }

    function render() {
      if (!vertices) return;
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

      let model = mat4Identity();
      model = mat4RotateX(model, rotX);
      model = mat4RotateY(model, rotY);
      const view = mat4Translate(mat4Identity(), 0, 0, -zoom);
      const proj = mat4Perspective(Math.PI / 4, canvas.width / canvas.height, 0.1, 100);
      const mvp = mat4Multiply(proj, mat4Multiply(view, model));

      gl.uniformMatrix4fv(uMVP, false, mvp);
      gl.uniformMatrix3fv(uNormalMatrix, false, mat3FromMat4(model));

      gl.drawArrays(gl.TRIANGLES, 0, vertices.length / 3);
    }

    /* Mouse interaction */
    canvas.addEventListener('mousedown', e => { dragging = true; lastX = e.clientX; lastY = e.clientY; });
    canvas.addEventListener('mousemove', e => {
      if (!dragging) return;
      rotY += (e.clientX - lastX) * 0.01;
      rotX += (e.clientY - lastY) * 0.01;
      lastX = e.clientX; lastY = e.clientY;
      render();
    });
    canvas.addEventListener('mouseup', () => { dragging = false; });
    canvas.addEventListener('mouseleave', () => { dragging = false; });
    canvas.addEventListener('wheel', e => {
      e.preventDefault();
      zoom = Math.max(0.5, Math.min(10, zoom + e.deltaY * 0.005));
      render();
    }, { passive: false });

    /* Touch interaction */
    canvas.addEventListener('touchstart', e => {
      if (e.touches.length === 1) {
        dragging = true; lastX = e.touches[0].clientX; lastY = e.touches[0].clientY;
      }
    }, { passive: true });
    canvas.addEventListener('touchmove', e => {
      if (!dragging || e.touches.length !== 1) return;
      rotY += (e.touches[0].clientX - lastX) * 0.01;
      rotX += (e.touches[0].clientY - lastY) * 0.01;
      lastX = e.touches[0].clientX; lastY = e.touches[0].clientY;
      render();
    }, { passive: true });
    canvas.addEventListener('touchend', () => { dragging = false; }, { passive: true });

    /* Fetch and render STL */
    fetch(stlUrl)
      .then(r => r.arrayBuffer())
      .then(buffer => {
        const parsed = parseSTL(buffer);
        normalizeVertices(parsed.verts);
        vertices = parsed.verts;
        normals = parsed.norms;

        const vBuf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, vBuf);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);

        const nBuf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, nBuf);
        gl.bufferData(gl.ARRAY_BUFFER, normals, gl.STATIC_DRAW);
        gl.enableVertexAttribArray(aNorm);
        gl.vertexAttribPointer(aNorm, 3, gl.FLOAT, false, 0, 0);

        render();
      })
      .catch(err => {
        console.error('STL load error:', err);
        canvas.parentElement.innerHTML = '<p style="padding:20px;text-align:center;color:#6b7280;">Failed to load 3D model. Please download the file instead.</p>';
      });
  }

  /* ── Initialize all PDP modules ── */
  function init() {
    const container = document.querySelector('.wq-pdp');
    if (!container) return;

    initTabs(container);
    initGallery(container);
    initQuantity(container);
    initATC(container);
    initVariants(container);
    initPreviewToggles(container);
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init };
})();
